import { useState } from 'react'
import { Form, Input, Button, Alert, theme } from 'antd'
import { UserAddOutlined, ArrowLeftOutlined, CheckCircleFilled } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '@/lib/axios'

const RegisterRequestPage = () => {
  const { token } = theme.useToken()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const onFinish = async (values: Record<string, string>) => {
    setLoading(true)
    setError(null)
    try {
      await axiosInstance.post('/auth/register-request', values)
      setSubmitted(true)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail || 'Errore durante l\'invio della richiesta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>

      {/* ── Pannello Brand ── */}
      <div style={{
        flex: '0 0 42%',
        background: '#0d1b2e',
        backgroundImage: [
          'radial-gradient(circle, rgba(24,144,255,0.18) 1px, transparent 1px)',
          'linear-gradient(180deg, rgba(24,144,255,0.06) 0%, transparent 60%)',
        ].join(', '),
        backgroundSize: '28px 28px, 100% 100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px 44px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Accent line */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          background: 'linear-gradient(180deg, #1890ff 0%, rgba(24,144,255,0.2) 100%)',
        }} />

        {/* Logo */}
        <div>
          <img
            src="/Tiber-Garage.png"
            alt="Tiber Garage"
            style={{ height: 52, objectFit: 'contain', marginBottom: 48 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />

          <div style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#1890ff',
            marginBottom: 12,
          }}>
            Richiesta di accesso
          </div>

          <h1 style={{
            fontSize: 34,
            fontWeight: 800,
            lineHeight: 1.1,
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.02em',
          }}>
            Entra a far<br />
            <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 300 }}>
              parte del team.
            </span>
          </h1>

          <p style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.5)',
            lineHeight: 1.6,
            margin: '0 0 40px',
            maxWidth: 280,
          }}>
            Compila il modulo e l'amministratore attiverà il tuo account. Riceverai una notifica via email.
          </p>

          <div style={{
            padding: '16px 20px',
            background: 'rgba(24,144,255,0.1)',
            borderRadius: 8,
            borderLeft: '3px solid #1890ff',
          }}>
            <p style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.6)',
              margin: 0,
              lineHeight: 1.5,
            }}>
              Accesso invite-only. La richiesta sarà revisionata entro 24h lavorative.
            </p>
          </div>
        </div>

        {/* Footer brand */}
        <div style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.2)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          Tiber Garage © {new Date().getFullYear()}
        </div>
      </div>

      {/* ── Pannello Form ── */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-container)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 32px',
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>

          {submitted ? (
            /* ── Stato successo ── */
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 72,
                height: 72,
                background: token.colorSuccessBg,
                border: `2px solid ${token.colorSuccessBorder}`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}>
                <CheckCircleFilled style={{ fontSize: 32, color: '#52c41a' }} />
              </div>
              <h2 style={{
                fontSize: 24,
                fontWeight: 700,
                color: 'var(--color-text)',
                margin: '0 0 12px',
              }}>
                Richiesta inviata
              </h2>
              <p style={{
                fontSize: 14,
                color: 'var(--color-text-secondary)',
                lineHeight: 1.6,
                margin: '0 0 32px',
              }}>
                Riceverai una email quando l'amministratore avrà attivato il tuo account.
              </p>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/')}
              >
                Torna al login
              </Button>
            </div>
          ) : (
            /* ── Form ── */
            <>
              <div style={{ marginBottom: 32 }}>
                <h2 style={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  margin: '0 0 8px',
                  letterSpacing: '-0.01em',
                }}>
                  Crea il tuo account
                </h2>
                <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
                  Inserisci i tuoi dati per richiedere l'accesso.
                </p>
              </div>

              {error && (
                <Alert
                  type="error"
                  message={error}
                  showIcon
                  style={{ marginBottom: 20 }}
                />
              )}

              <Form layout="vertical" onFinish={onFinish}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <Form.Item
                    name="nome"
                    label="Nome"
                    rules={[{ required: true, message: 'Inserisci il nome' }]}
                  >
                    <Input placeholder="Mario" size="large" />
                  </Form.Item>

                  <Form.Item
                    name="cognome"
                    label="Cognome"
                    rules={[{ required: true, message: 'Inserisci il cognome' }]}
                  >
                    <Input placeholder="Rossi" size="large" />
                  </Form.Item>
                </div>

                <Form.Item
                  name="username"
                  label="Username"
                  rules={[
                    { required: true, message: 'Inserisci lo username' },
                    { min: 3, message: 'Minimo 3 caratteri' },
                  ]}
                >
                  <Input placeholder="mario.rossi" size="large" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Inserisci la email' },
                    { type: 'email', message: 'Email non valida' },
                  ]}
                >
                  <Input placeholder="mario.rossi@example.com" size="large" />
                </Form.Item>

                <Form.Item name="messaggio" label="Note per l'amministratore (opzionale)">
                  <Input.TextArea
                    rows={3}
                    placeholder="Ruolo, officina di appartenenza o altro..."
                    size="large"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      size="large"
                      onClick={() => navigate('/')}
                      style={{ flex: '0 0 auto' }}
                    >
                      Indietro
                    </Button>
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      htmlType="submit"
                      loading={loading}
                      size="large"
                      style={{ flex: 1, fontWeight: 600 }}
                    >
                      Invia richiesta
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RegisterRequestPage
